import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ClipLoader from 'react-spinners/ClipLoader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpa } from '@fortawesome/free-solid-svg-icons'
import { isEmailValid, isPasswordValid } from '../utils'
import { ResearcherInviteData, SignupRequest } from '../types/auth'
import { useLazyGetInviteRequestDataByTokenQuery, useRegisterOrganizationMutation } from '../store/api/authApi'
import { AuthError } from '../errors'

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [registerOrganization] = useRegisterOrganizationMutation()

  const [getInviteRequestDataByToken] = useLazyGetInviteRequestDataByTokenQuery()

  const [email, setEmail] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  const [doi, setDoi] = useState<string>('')
  const [organizationName, setOrganizationName] = useState<string>('')
  const [isEmailTouched, setIsEmailTouched] = useState<boolean>(false)
  const [isPasswordTouched, setIsPasswordTouched] = useState<boolean>(false)
  const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState<boolean>(false)
  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)

  const [isResearcherFlow, setIsResearcherFlow] = useState<boolean>(false)

  const invitationToken = useMemo(() => {
    return searchParams.get('token')
  }, [searchParams])

  const getInviteData = useCallback(async () => {
    if (invitationToken) {
      const res: ResearcherInviteData = await getInviteRequestDataByToken(invitationToken).unwrap()

      if (!res) {
        toast.error('Invite request not found for the token')

        return
      }

      const isDataValid = res.email && res.firstName && res.lastName && res.organizationDoi && res.organizationName

      if (!isDataValid) {
        toast.error('Invite request data is invalid, please contact the administrator')
      }

      setEmail(res.email)
      setFirstName(res.firstName)
      setLastName(res.lastName)
      setDoi(res.organizationDoi)
      setOrganizationName(res.organizationName)
      setIsResearcherFlow(true)
    }
  }, [invitationToken])

  useEffect(() => {
    getInviteData()
  }, [getInviteData])

  const submitRegistrationRequest = useCallback(async () => {
    const request: SignupRequest = {
      email,
      password,
      firstName,
      lastName,
      doi,
      organizationName,
      token: invitationToken ?? undefined,
    }

    try {
      setIsApiRequestInProgress(true)

      await registerOrganization({ data: request }).unwrap()

      toast('Successfully registered')
      setIsApiRequestInProgress(false)
      navigate('/login')
    } catch (e: any) {
      console.error(e)

      if (e.name === 'AuthError') {
        toast.error(e.message)
      } else {
        toast.error('Failed to register')
      }

      setIsApiRequestInProgress(false)
    }
  }, [firstName, lastName, email, password, doi, registerOrganization])

  const showPasswordFormatHint = useMemo(() => {
    return isPasswordTouched && !isPasswordValid(password)
  }, [password])

  const isFormValid = useMemo(() => {
    return (
      firstName &&
      lastName &&
      organizationName &&
      doi &&
      isEmailValid(email) &&
      isPasswordValid(password) &&
      password === confirmPassword
    )
  }, [firstName, lastName, organizationName, email, password, confirmPassword, doi])

  return (
    <main className="main" id="top">
      <div className="container">
        <div className="row flex-center min-vh-100 py-5">
          <div className="d-flex flex-column col-sm-10 col-md-8 col-lg-5 col-xl-5 col-xxl-3">
            <button
              type="button"
              className="btn-link d-flex flex-center text-decoration-none mb-4"
              onClick={() => navigate('/')}
            >
              <div className="d-flex align-items-center fw-bolder fs-5 d-inline-block">
                <FontAwesomeIcon
                  icon={faSpa}
                  className="text-warning-300 fs-5 "
                  style={{ width: '60px', height: '60px' }}
                />
              </div>
            </button>
            <div className="text-center mb-7">
              <h3 className="text-1000 ">Sign Up</h3>
              <p className="text-700">Create your account today</p>
            </div>

            <form>
              <div className="mb-3 text-start">
                <label className="form-label" htmlFor="orgId">
                  Organization DOI
                </label>

                <input
                  className="form-control"
                  id="orgId"
                  type="text"
                  placeholder="Organization DOI"
                  value={doi}
                  onChange={(e) => {
                    setDoi(e.target.value)
                  }}
                  readOnly={isResearcherFlow}
                />
                {/*
                    <a className="fs--1 fw-bold text-primary" style={{ paddingLeft: '12px' }}>
                      <span className="text-primary" data-feather="search"></span>
                      Look up an organization
                    </a>
                  */}
              </div>

              <div className="mb-3 text-start">
                <label className="form-label" htmlFor="orgName">
                  Organization Name
                </label>
                <input
                  className="form-control"
                  id="orgName"
                  type="text"
                  placeholder="Organization Name"
                  value={organizationName}
                  onChange={(e) => {
                    setOrganizationName(e.target.value)
                  }}
                  readOnly={isResearcherFlow}
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <label className="form-label" htmlFor="firstName">
                    First Name
                  </label>
                  <input
                    className="form-control"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value)
                    }}
                    type="text"
                    placeholder="First Name"
                  />
                </div>

                <div className="col-sm-6">
                  <label className="form-label" htmlFor="lastName">
                    Last Name
                  </label>
                  <input
                    className="form-control"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value)
                    }}
                    type="text"
                    placeholder="Last Name"
                  />
                </div>
              </div>

              <div className="mb-3 text-start">
                <label className="form-label" htmlFor="email">
                  Email Address
                </label>
                <input
                  className="form-control"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setIsEmailTouched(true)
                  }}
                  placeholder="name@example.com"
                  readOnly={isResearcherFlow}
                />
                {isEmailTouched && !isEmailValid(email) ? (
                  <div className="hint error mt-3">Wrong email format</div>
                ) : null}
              </div>
              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <label className="form-label" htmlFor="password">
                    Password
                  </label>
                  <input
                    className="form-control form-icon-input"
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setIsPasswordTouched(true)
                    }}
                    type="password"
                    placeholder="Password"
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <input
                    className="form-control form-icon-input"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      setIsConfirmPasswordTouched(true)
                    }}
                    type="password"
                    placeholder="Confirm Password"
                  />
                </div>
                {showPasswordFormatHint ? <div className="hint error">Wrong password format</div> : null}
                {!showPasswordFormatHint && (isConfirmPasswordTouched || password) && password !== confirmPassword ? (
                  <div className="hint error">Passwords do not match</div>
                ) : null}
                <div className="col-sm-12 text-600 small">
                  Password must contain at least 8 characters, a lowercase letter, an uppercase letter, a digit and a
                  special symbols (! @ # $ % ^ &)
                </div>
              </div>
              <button
                type="button"
                className="btn btn-primary w-100 mb-3"
                onClick={submitRegistrationRequest}
                disabled={!isFormValid}
              >
                {isApiRequestInProgress ? (
                  <ClipLoader color="white" loading={isApiRequestInProgress} size={15} />
                ) : (
                  'Register'
                )}
              </button>
              <div className="text-center">
                <a className="fs--1 fw-bold pointer" onClick={() => navigate('/login')}>
                  Sign in to an existing account
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  )
}
